import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import { Dialog } from '@/components/AppDialog'
import { useEffect, useState } from 'react'
import {
  useCloseEbReading,
  useCorrectEbReading,
  useHalls,
  useOpenEbReading,
  useOrders,
} from '@/api/hooks'
import type { EbReading } from '@/api/types'
import { ErrorBanner, formatDateTime, formatMoney, formatNumber } from '@/components/common'

/** The booking a reading is being taken for, when the caller already knows it. */
export type ReadingOrder = {
  id: string
  orderNumber: string
  customerName: string
  hallId?: string
}

/**
 * Opens a meter against a banquet booking.
 *
 * <p>Raised either from the EB register, where the hall and booking are chosen,
 * or from the order itself, where both are already known - the same reading
 * either way, so the same form takes it.</p>
 */
export function OpeningDialog({
  open,
  order,
  onClose,
}: {
  open: boolean
  /** Fixed when taken from an order; omitted when taken from the register. */
  order?: ReadingOrder
  onClose: () => void
}) {
  const { data: halls, refetch: refetchHalls } = useHalls()
  // Only fetch the picker's orders when the caller has not already fixed one.
  const { data: orders } = useOrders(order ? { size: 1, status: 'CONFIRMED' } : { status: 'CONFIRMED', size: 100 })
  const openReading = useOpenEbReading()

  const [hallId, setHallId] = useState(order?.hallId ?? '')
  // One figure per board, where the hall has more than one - a hall wired
  // with three meters is one form and one submit, not this dialog opened
  // three times over with a different board picked each time.
  const [readingsByMeter, setReadingsByMeter] = useState<Record<string, string>>({})
  const [orderId, setOrderId] = useState(order?.id ?? '')
  const [reading, setReading] = useState('')
  const [remarks, setRemarks] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [failedMeterName, setFailedMeterName] = useState('')

  useEffect(() => {
    if (!open) return
    setOrderId(order?.id ?? '')
    setHallId(order?.hallId ?? '')
    setReadingsByMeter({})
    setReading('')
    setRemarks('')
    setFailedMeterName('')
    // The hall list this dialog has been holding onto may well be older than
    // a meter added five minutes ago in Settings, in another tab or earlier
    // on this same page - read fresh the moment the form is opened, rather
    // than whatever happened to be cached from whenever the page loaded.
    void refetchHalls()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, order])

  const hall = halls?.find((candidate) => candidate.id === hallId)
  const meters = hall?.meters ?? []
  const filled = meters.filter((meter) => (readingsByMeter[meter.id] ?? '').trim() !== '')

  const submit = async () => {
    if (meters.length > 0) {
      setSubmitting(true)
      setFailedMeterName('')
      let current: string | null = null
      try {
        // One request per meter, in the order they are listed - each its own
        // reading, so a board that fails partway leaves the others already
        // recorded rather than losing the lot to one bad figure.
        for (const meter of filled) {
          current = meter.name
          // eslint-disable-next-line no-await-in-loop
          await openReading.mutateAsync({
            hallId,
            hallMeterId: meter.id,
            orderId: orderId || undefined,
            openingReading: Number(readingsByMeter[meter.id]),
            remarks: remarks || undefined,
          })
        }
        onClose()
      } catch {
        setFailedMeterName(current ?? '')
      } finally {
        setSubmitting(false)
      }
      return
    }
    openReading.mutate(
      {
        hallId,
        orderId: orderId || undefined,
        openingReading: Number(reading),
        // The meter, its tariff and its multiplying factor belong to the hall.
        // Asking for them again invites a different answer, and the reading is
        // priced on whatever was typed rather than on what the hall charges.
        remarks: remarks || undefined,
      },
      { onSuccess: onClose },
    )
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Opening meter entry</DialogTitle>
      <DialogContent>
        <ErrorBanner error={openReading.error} />
        <Alert severity="info" sx={{ mb: 2 }}>
          Take this when the hall is handed over. The tariff is fixed now, so a later revision cannot change
          what the guest was quoted.
        </Alert>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Hall"
              value={hallId}
              onChange={(event) => setHallId(event.target.value)}
            >
              {(halls ?? []).map((candidate) => (
                <MenuItem key={candidate.id} value={candidate.id}>
                  {candidate.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            {order ? (
              <TextField
                fullWidth
                label="Order"
                value={`${order.orderNumber} — ${order.customerName}`}
                disabled
                helperText="Taken against this booking"
              />
            ) : (
              <TextField
                select
                fullWidth
                label="Order"
                value={orderId}
                onChange={(event) => setOrderId(event.target.value)}
              >
                <MenuItem value="">Not linked</MenuItem>
                {(orders?.content ?? []).map((candidate) => (
                  <MenuItem key={candidate.id} value={candidate.id}>
                    {candidate.orderNumber} — {candidate.customerName}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Grid>
          {/* A hall with its boards set up: one field per meter, all taken
              down in the one form and recorded as separate readings on
              submit. A hall with none keeps the single field it always had. */}
          {meters.length > 0 ? (
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Opening reading, by meter
              </Typography>
              <Grid container spacing={2}>
                {meters.map((meter) => (
                  <Grid item xs={12} key={meter.id}>
                    <TextField
                      fullWidth
                      type="number"
                      label={meter.name}
                      value={readingsByMeter[meter.id] ?? ''}
                      onChange={(event) =>
                        setReadingsByMeter((current) => ({ ...current, [meter.id]: event.target.value }))
                      }
                      // The rate is the hall's own, the same for every board on
                      // it; only the gearing differs meter to meter.
                      helperText={[
                        meter.meterNumber,
                        Number(meter.multiplyingFactor) !== 1 ? `MFC ×${meter.multiplyingFactor}` : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    />
                  </Grid>
                ))}
              </Grid>
              {hall ? (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Billed at {formatMoney(hall.ebRatePerUnit, true)} per unit, the hall's own tariff for every
                  meter on it; correct it in Settings if it is wrong.
                </Typography>
              ) : null}
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Leave a meter blank to skip it for now — an opening entry can still be taken for it later.
              </Typography>
            </Grid>
          ) : (
            <>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Opening reading"
                  value={reading}
                  onChange={(event) => setReading(event.target.value)}
                />
              </Grid>
              {hall ? (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    {/* Precise: a tariff of 9.75 rounded to ten reads as a
                        different rate from the one the reading will actually
                        be priced at. */}
                    Meter {hall.ebMeterNumber ?? 'not named on this hall'} · billed at{' '}
                    {formatMoney(hall.ebRatePerUnit, true)} per unit · MFC{' '}
                    {formatNumber(hall.ebMultiplyingFactor ?? 1)}
                    {(hall.ebMultiplyingFactor ?? 1) === 1
                      ? ' (ungeared)'
                      : `, so the dials are multiplied by ${hall.ebMultiplyingFactor}`}
                    . All three come from the hall; correct them in Settings if they are wrong.
                  </Typography>
                </Grid>
              ) : null}
            </>
          )}
          {failedMeterName ? (
            <Grid item xs={12}>
              <Alert severity="warning">
                Stopped at {failedMeterName} — the meters above it are already recorded. Fix the figure and
                try again; the ones already saved will not be repeated.
              </Alert>
            </Grid>
          ) : null}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Remarks"
              value={remarks}
              onChange={(event) => setRemarks(event.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => void submit()}
          disabled={
            !hallId
            || (meters.length > 0 ? filled.length === 0 : !reading)
            || submitting
            || openReading.isPending
          }
        >
          {submitting ? 'Recording…' : 'Record opening'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/**
 * Corrects a reading that was keyed wrongly.
 *
 * <p>A meter is read by somebody standing in front of it with a phone, and a
 * digit gets dropped. Until the reading has been invoiced it is ours to put
 * right - both figures, the multiplying factor and the rate, because a wrong
 * tariff is as wrong as a wrong dial. Once it is on an invoice the guest has
 * been charged, and the way back is a credit note, which is what the server
 * says if it is tried.</p>
 *
 * <p>A reason is required. A meter reading that changed with nothing beside it
 * saying why is the one an audit stops at.</p>
 */
export function CorrectionDialog({
  reading,
  onClose,
}: {
  reading: EbReading | null
  onClose: () => void
}) {
  const correct = useCorrectEbReading()
  const { data: halls } = useHalls()
  const [opening, setOpening] = useState('')
  const [closing, setClosing] = useState('')
  const [factor, setFactor] = useState('')
  const [rate, setRate] = useState('')
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (!reading) return
    setOpening(reading.openingReading != null ? String(reading.openingReading) : '')
    setClosing(reading.closingReading != null ? String(reading.closingReading) : '')
    setFactor(String(reading.multiplyingFactor || 1))
    setRate(String(reading.ratePerUnit ?? 0))
    setReason('')
  }, [reading?.id])

  if (!reading) return null

  // What the hall says today. A reading opened before somebody set the MFC
  // carries the old one, and the office has no way of knowing that from
  // looking at it - so the difference is named rather than left to be noticed.
  const hallFactor = halls?.find((candidate) => candidate.id === reading.hallId)
    ?.ebMultiplyingFactor ?? 1
  const stale = factor !== '' && Number(factor) !== Number(hallFactor)

  const belowOpening =
    opening !== '' && closing !== '' && Number(closing) < Number(opening)
  const units =
    opening !== '' && closing !== '' && !belowOpening
      ? (Number(closing) - Number(opening)) * (Number(factor) || 1)
      : null
  const amount = units == null ? null : units * (Number(rate) || 0)
  const wasAmount = reading.amount

  const submit = () =>
    correct.mutate(
      {
        id: reading.id,
        body: {
          openingReading: opening === '' ? undefined : Number(opening),
          closingReading: closing === '' ? undefined : Number(closing),
          multiplyingFactor: factor === '' ? undefined : Number(factor),
          ratePerUnit: rate === '' ? undefined : Number(rate),
          reason: reason.trim(),
        },
      },
      { onSuccess: onClose },
    )

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Correct {reading.readingNumber}</DialogTitle>
      <DialogContent>
        <ErrorBanner error={correct.error} />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {reading.hallName} · meter {reading.meterNumber ?? 'not recorded'} ·{' '}
          {reading.orderNumber ?? 'no order'}
        </Typography>

        {stale ? (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => setFactor(String(hallFactor))}>
                Use {hallFactor}
              </Button>
            }
          >
            This reading was taken at MFC {factor}. The hall master says {hallFactor} — a reading
            keeps the factor its meter was opened with, so one opened before the master was set
            still carries the old figure.
          </Alert>
        ) : null}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Opening reading"
              value={opening}
              onChange={(event) => setOpening(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Closing reading"
              value={closing}
              onChange={(event) => setClosing(event.target.value)}
              error={belowOpening}
              helperText={belowOpening ? 'Cannot be below the opening reading' : ' '}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="MFC"
              value={factor}
              onChange={(event) => setFactor(event.target.value)}
              helperText={
                stale
                  ? `${hallFactor} on the hall master — click to use it`
                  : 'Multiplying factor, off the hall master'
              }
              FormHelperTextProps={
                stale
                  ? {
                      onClick: () => setFactor(String(hallFactor)),
                      sx: { cursor: 'pointer', color: 'warning.main', fontWeight: 600 },
                    }
                  : undefined
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Rate per unit"
              value={rate}
              onChange={(event) => setRate(event.target.value)}
              helperText="The hall tariff, unless it was agreed otherwise"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Why it is being corrected"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Closing dial read 2000, entered as 200"
            />
          </Grid>
        </Grid>

        {amount != null ? (
          <Alert severity={amount === wasAmount ? 'info' : 'warning'} sx={{ mt: 2 }}>
            {formatNumber(units)} units at {formatMoney(Number(rate) || 0)} ={' '}
            <strong>{formatMoney(amount)}</strong>
            {amount === wasAmount ? null : (
              <>
                {' '}
                — was {formatMoney(wasAmount)}.
              </>
            )}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!reason.trim() || belowOpening || correct.isPending}
        >
          Save correction
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/**
 * Closes one or several readings - a hall opened on three meters hands all
 * three back closed in the one form, each its own figure, one submit.
 */
export function ClosingDialog({ readings, onClose }: { readings: EbReading[]; onClose: () => void }) {
  const closeReading = useCloseEbReading()
  const [valueByReading, setValueByReading] = useState<Record<string, string>>({})
  const [remarks, setRemarks] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [failedReadingNumber, setFailedReadingNumber] = useState('')

  const key = readings.map((r) => r.id).join(',')
  useEffect(() => {
    setValueByReading({})
    setRemarks('')
    setFailedReadingNumber('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  if (readings.length === 0) return null
  const single = readings.length === 1

  const belowOpening = (reading: EbReading) => {
    const value = valueByReading[reading.id] ?? ''
    return value !== '' && reading.openingReading != null && Number(value) < reading.openingReading
  }
  const unitsFor = (reading: EbReading) => {
    const value = valueByReading[reading.id]
    if (!value || reading.openingReading == null || belowOpening(reading)) return null
    return (Number(value) - reading.openingReading) * (reading.multiplyingFactor || 1)
  }

  const filled = readings.filter((reading) => (valueByReading[reading.id] ?? '').trim() !== '')
  const anyBelowOpening = filled.some(belowOpening)

  const submit = async () => {
    setSubmitting(true)
    setFailedReadingNumber('')
    let current = ''
    try {
      // One request per meter, in the order they are listed - so a figure
      // that is refused leaves the readings above it already closed.
      for (const reading of filled) {
        current = reading.readingNumber
        // eslint-disable-next-line no-await-in-loop
        await closeReading.mutateAsync({
          id: reading.id,
          body: { closingReading: Number(valueByReading[reading.id]), remarks: remarks || undefined },
        })
      }
      onClose()
    } catch {
      setFailedReadingNumber(current)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {single ? `Closing entry — ${readings[0].readingNumber}` : `Closing entries — ${readings.length} meters`}
      </DialogTitle>
      <DialogContent>
        <ErrorBanner error={closeReading.error} />
        {readings.map((reading) => {
          const below = belowOpening(reading)
          const units = unitsFor(reading)
          const amount = units == null ? null : units * reading.ratePerUnit
          return (
            <Box key={reading.id} sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {reading.hallName} · meter {reading.meterNumber ?? '—'} · opened at{' '}
                {formatNumber(reading.openingReading)} on {formatDateTime(reading.openingAt)}
              </Typography>
              <TextField
                fullWidth
                type="number"
                label="Closing reading"
                value={valueByReading[reading.id] ?? ''}
                onChange={(event) =>
                  setValueByReading((current) => ({ ...current, [reading.id]: event.target.value }))
                }
                error={below}
                helperText={below ? 'Cannot be below the opening reading' : ' '}
                sx={{ mt: 1 }}
              />
              {units != null && !below ? (
                <Typography variant="caption" color="success.main" display="block">
                  {formatNumber(units)} units at {formatMoney(reading.ratePerUnit)} ={' '}
                  <strong>{formatMoney(amount)}</strong> recoverable from the guest.
                </Typography>
              ) : null}
            </Box>
          )
        })}

        <TextField
          fullWidth
          label="Remarks"
          value={remarks}
          onChange={(event) => setRemarks(event.target.value)}
        />

        {failedReadingNumber ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Stopped at {failedReadingNumber} — the readings above it are already closed. Fix the figure and
            try again; the ones already saved will not be repeated.
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => void submit()}
          disabled={filled.length === 0 || anyBelowOpening || submitting || closeReading.isPending}
        >
          {submitting ? 'Recording…' : 'Record closing'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
